import { useEffect, useRef } from "react";

/**
 * Fond animé de l'en-tête de la page Portefeuilles : une constellation d'actifs
 * qui dérivent lentement et se relient quand ils se rapprochent.
 * Les noms sont des actifs génériques connus de tous : ils ne révèlent rien du contenu des portefeuilles.
 * - s'estompe vers la gauche pour ne pas gêner le titre (masque CSS)
 * - image fixe si l'utilisateur a demandé moins d'animations (prefers-reduced-motion)
 * Le parent doit être en `position: relative`.
 */
const COLORS = ["0,191,99", "177,117,255", "247,152,55"];
const LABELS = ["S&P 500", "CAC 40", "NASDAQ", "OR", "PÉTROLE", "EUR/USD", "APPLE", "MICROSOFT", "TESLA"];
const COUNT = 30;
const LINK_DIST = 210;

export default function ConstellationBackground({ className = "" }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let width = 0;
    let height = 0;
    let last = 0;
    let frame = 0;

    // Positions en fraction de la zone ; les points restent dans les 65 % de droite
    const nodes = Array.from({ length: COUNT }, (_, i) => ({
      x: 0.35 + Math.random() * 0.65,
      y: Math.random(),
      vx: (Math.random() - 0.5) * 0.012,
      vy: (Math.random() - 0.5) * 0.012,
      r: 1.5 + Math.random() * 2.5,
      color: COLORS[i % COLORS.length],
      label: i < LABELS.length ? LABELS[i] : null,
    }));

    // Téléphone : moitié moins de points (sinon l'animation paraît brouillonne sur un petit écran)
    const visible = () => (width < 640 ? nodes.slice(0, 14) : nodes);

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      width = rect.width;
      height = rect.height;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const draw = (dt) => {
      ctx.clearRect(0, 0, width, height);
      const pts = visible().map((n) => {
        n.x += n.vx * dt;
        n.y += n.vy * dt;
        if (n.x < 0.35 || n.x > 1) n.vx *= -1;
        if (n.y < 0 || n.y > 1) n.vy *= -1;
        return [n, width * n.x, height * n.y];
      });
      for (let i = 0; i < pts.length; i += 1) {
        for (let j = i + 1; j < pts.length; j += 1) {
          const dist = Math.hypot(pts[i][1] - pts[j][1], pts[i][2] - pts[j][2]);
          if (dist < LINK_DIST) {
            ctx.beginPath();
            ctx.moveTo(pts[i][1], pts[i][2]);
            ctx.lineTo(pts[j][1], pts[j][2]);
            ctx.strokeStyle = `rgba(255,255,255,${0.12 * (1 - dist / LINK_DIST)})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }
      pts.forEach(([n, x, y], i) => {
        ctx.beginPath();
        ctx.arc(x, y, n.r, 0, Math.PI * 2);
        // Téléphone : un point sur trois en couleur, les autres en gris clair (plus sobre)
        ctx.fillStyle = width < 640 && i % 3 ? "rgba(255,255,255,0.35)" : `rgba(${n.color},0.6)`;
        ctx.fill();
        if (n.label && width >= 640) { // pas de noms sur téléphone : ils passeraient sous le texte
          ctx.font = "10px 'IBM Plex Mono', monospace";
          ctx.fillStyle = "rgba(255,255,255,0.28)";
          ctx.fillText(n.label, x + 7, y + 3);
        }
      });
    };

    const tick = (now) => {
      const dt = Math.min(0.1, (now - (last || now)) / 1000);
      last = now;
      draw(dt);
      frame = requestAnimationFrame(tick);
    };

    resize();
    draw(0);
    if (!reduceMotion) frame = requestAnimationFrame(tick);

    const observer = new ResizeObserver(() => {
      resize();
      draw(0);
    });
    observer.observe(canvas);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  const mask = "linear-gradient(90deg, transparent 0%, transparent 40%, #000 75%)";
  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={`hero-anim absolute inset-0 w-full h-full pointer-events-none ${className}`}
      style={{ WebkitMaskImage: mask, maskImage: mask }}
      data-testid="portfolios-constellation-bg"
    />
  );
}
